import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ClientAvatar } from "@/components/ClientAvatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, ChevronRight, User as UserIcon, ShieldOff, ShieldCheck, Calendar as CalIcon, Copy, Phone, MapPin, Trash2, Plus, Briefcase, Mic } from "lucide-react";
import { toast } from "sonner";

// Voice Strength tier helper — mirrors backend voice fingerprint thresholds.
// 0–1 signed substantive notes → "Building" (fingerprint not yet active)
// 2–4 → "Active"  (engine has 2-4 voice exemplars to mimic)
// 5+  → "Strong"  (full 5-sample context — peak fingerprint accuracy)
function voiceStrengthTier(count) {
  const c = Number(count || 0);
  if (c >= 5) return { label: "Strong", tone: "emerald", title: `${c} signed narratives — full voice fingerprint context.` };
  if (c >= 2) return { label: "Active", tone: "amber", title: `${c} signed narratives — fingerprint engaged.` };
  return { label: "Building", tone: "slate", title: `${c} signed narrative${c === 1 ? "" : "s"} — needs ${2 - c} more to activate voice fingerprint.` };
}

function VoiceStrengthBadge({ count }) {
  const tier = voiceStrengthTier(count);
  const styles = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    slate: "bg-slate-50 text-slate-600 border-slate-200",
  }[tier.tone];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${styles}`}
      title={tier.title}
      data-testid={`voice-strength-${tier.label.toLowerCase()}`}
    >
      <Mic className="w-3 h-3" />
      {tier.label}
      <span className="text-[10px] opacity-70 normal-case font-normal">({count || 0})</span>
    </span>
  );
}

function AssignRespiteDialog({ clients, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    respite_provider_name: "", respite_provider_email: "",
    client_id: "", start_date: "", end_date: "",
  });
  const [setupUrl, setSetupUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.respite_provider_name || !form.respite_provider_email || !form.client_id || !form.start_date || !form.end_date) {
      return toast.error("All fields are required");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/respite/assignments", form);
      const origin = window.location.origin;
      const url = `${origin}/login?setup=${data.setup_token}`;
      setSetupUrl(url);
      toast.success("Respite assignment created. Share the setup link with the respite provider.");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reset = () => {
    setForm({ respite_provider_name: "", respite_provider_email: "", client_id: "", start_date: "", end_date: "" });
    setSetupUrl("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="open-assign-respite">
          <CalIcon className="w-4 h-4 mr-2" /> Assign Temporary Respite
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Assign Temporary Respite Provider</DialogTitle>
          <DialogDescription>
            Provision a short-term respite account scoped to a single client and date window.
            The primary contractor will be auto-locked for the same dates.
          </DialogDescription>
        </DialogHeader>
        {!setupUrl && (
          <div className="space-y-3">
            <div>
              <Label>Respite Provider Name</Label>
              <Input value={form.respite_provider_name}
                     onChange={(e) => setForm({ ...form, respite_provider_name: e.target.value })}
                     data-testid="respite-name" />
            </div>
            <div>
              <Label>Respite Provider Email</Label>
              <Input type="email" value={form.respite_provider_email}
                     onChange={(e) => setForm({ ...form, respite_provider_email: e.target.value })}
                     data-testid="respite-email" />
            </div>
            <div>
              <Label>Target Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="respite-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date}
                       onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                       data-testid="respite-start" />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={form.end_date}
                       onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                       data-testid="respite-end" />
              </div>
            </div>
          </div>
        )}
        {setupUrl && (
          <div className="space-y-3">
            <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm">
              <p className="font-semibold text-emerald-900 mb-1">Provisioning email queued.</p>
              <p className="text-emerald-800">
                Share this one-time setup link (valid 7 days) with{" "}
                <span className="font-semibold">{form.respite_provider_email}</span> so they can set their password.
              </p>
            </div>
            <div className="bg-secondary rounded-md p-3 break-all text-xs flex items-start gap-2">
              <code className="flex-1" data-testid="respite-setup-url">{setupUrl}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(setupUrl); toast.success("Copied"); }}
                className="text-primary"
                data-testid="copy-setup-url"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        <DialogFooter>
          {!setupUrl ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="respite-submit">
                {busy ? "Provisioning..." : "Send provisioning email"}
              </Button>
            </>
          ) : (
            <Button onClick={() => setOpen(false)} data-testid="respite-done">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function newRowId() {
  return crypto.randomUUID();
}

function AddContractorDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    provider_name: "", provider_email: "", provider_password: "",
    address: "",
    phones: [{ id: newRowId(), label: "Mobile", number: "" }],
    household_members: [],
  });
  const [busy, setBusy] = useState(false);

  const reset = () => setForm({
    provider_name: "", provider_email: "", provider_password: "",
    address: "",
    phones: [{ id: newRowId(), label: "Mobile", number: "" }],
    household_members: [],
  });

  const submit = async () => {
    if (!form.provider_name.trim()) return toast.error("Name is required");
    if (!form.provider_email.trim()) return toast.error("Email is required");
    if (!form.provider_password || form.provider_password.length < 6) return toast.error("Password must be 6+ characters");
    setBusy(true);
    try {
      const cleanPhones = (form.phones || []).filter((p) => (p.number || "").trim());
      const cleanMembers = (form.household_members || []).filter((m) => (m.name || "").trim());
      await api.post("/setup/unified", {
        ...form,
        phones: cleanPhones,
        household_members: cleanMembers,
      });
      toast.success("Contractor created");
      setOpen(false);
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const updatePhone = (idx, patch) => setForm((f) => ({
    ...f,
    phones: f.phones.map((p, i) => i === idx ? { ...p, ...patch } : p),
  }));
  const removePhone = (idx) => setForm((f) => ({ ...f, phones: f.phones.filter((_, i) => i !== idx) }));
  const addPhone = () => setForm((f) => ({ ...f, phones: [...f.phones, { id: newRowId(), label: "Home", number: "" }] }));

  const updateMember = (idx, patch) => setForm((f) => ({
    ...f,
    household_members: f.household_members.map((m, i) => i === idx ? { ...m, ...patch } : m),
  }));
  const removeMember = (idx) => setForm((f) => ({ ...f, household_members: f.household_members.filter((_, i) => i !== idx) }));
  const addMember = () => setForm((f) => ({
    ...f,
    household_members: [...f.household_members, { id: newRowId(), name: "", relation: "", dob: "" }],
  }));

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="open-unified-add"><UserPlus className="w-4 h-4 mr-2" /> Add Contractor</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">New Contractor</DialogTitle>
          <DialogDescription>
            Each Contractor is a full host-family profile. Provision their login + address +
            phones + household roster in one step.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <p className="text-label">Login Account</p>
            <Input placeholder="Contractor full name" value={form.provider_name}
                   onChange={(e) => setForm({ ...form, provider_name: e.target.value })}
                   data-testid="unified-prov-name" />
            <Input placeholder="Email" type="email" value={form.provider_email}
                   onChange={(e) => setForm({ ...form, provider_email: e.target.value })}
                   data-testid="unified-prov-email" />
            <Input placeholder="Password (min 6 chars)" type="password" value={form.provider_password}
                   onChange={(e) => setForm({ ...form, provider_password: e.target.value })}
                   data-testid="unified-prov-password" />
          </div>

          <div className="space-y-2">
            <p className="text-label">Full Address</p>
            <Input placeholder="Street, City, State, ZIP" value={form.address}
                   onChange={(e) => setForm({ ...form, address: e.target.value })}
                   data-testid="unified-address" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-label">Phone Numbers</p>
              <Button type="button" size="sm" variant="outline" onClick={addPhone} data-testid="add-phone-row"><Plus className="w-3.5 h-3.5 mr-1" /> Add phone</Button>
            </div>
            {form.phones.map((p, idx) => (
              <div key={p.id || idx} className="grid grid-cols-[120px_1fr_auto] gap-2 items-center" data-testid={`phone-row-${idx}`}>
                <Input placeholder="Label" value={p.label}
                       onChange={(e) => updatePhone(idx, { label: e.target.value })} />
                <Input placeholder="(555) 123-4567" value={p.number}
                       onChange={(e) => updatePhone(idx, { number: e.target.value })} />
                <Button type="button" variant="ghost" size="sm" onClick={() => removePhone(idx)}>
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-label">Household Members</p>
              <Button type="button" size="sm" variant="outline" onClick={addMember} data-testid="add-member-row"><Plus className="w-3.5 h-3.5 mr-1" /> Add member</Button>
            </div>
            {form.household_members.length === 0 && (
              <p className="text-xs text-muted-foreground">No household members yet. Add anyone living in the home (spouse, kids, other residents).</p>
            )}
            {form.household_members.map((m, idx) => (
              <div key={m.id} className="grid grid-cols-[1fr_1fr_140px_auto] gap-2 items-center" data-testid={`member-row-${idx}`}>
                <Input placeholder="Name" value={m.name}
                       onChange={(e) => updateMember(idx, { name: e.target.value })} />
                <Input placeholder="Relation" value={m.relation}
                       onChange={(e) => updateMember(idx, { relation: e.target.value })} />
                <Input type="date" value={m.dob}
                       onChange={(e) => updateMember(idx, { dob: e.target.value })} />
                <Button type="button" variant="ghost" size="sm" onClick={() => removeMember(idx)}>
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="unified-submit">{busy ? "Saving..." : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddClientDialog({ contractors, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contractor_id: "" });
  const submit = async () => {
    if (!form.name || !form.contractor_id) return toast.error("Name and contractor required");
    await api.post("/clients", form);
    setForm({ name: "", contractor_id: "" });
    setOpen(false);
    onCreated?.();
    toast.success("Client added");
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="open-add-client"><UserPlus className="w-4 h-4 mr-2" /> Add Client</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">Add Client</DialogTitle>
          <DialogDescription>Add a new individual under a Contractor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input placeholder="Client name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-client-name" />
          <Select value={form.contractor_id} onValueChange={(v) => setForm({ ...form, contractor_id: v })}>
            <SelectTrigger data-testid="add-client-contractor"><SelectValue placeholder="Contractor" /></SelectTrigger>
            <SelectContent>{contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="add-client-submit">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminProfiles() {
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);
  const [clients, setClients] = useState([]);
  const [respite, setRespite] = useState([]);
  const [voiceStrength, setVoiceStrength] = useState({});

  const load = async () => {
    const [c, cl, r, vs] = await Promise.all([
      api.get("/users/providers"),
      api.get("/clients"),
      api.get("/respite/assignments"),
      api.get("/admin/voice-strength").catch(() => ({ data: {} })),
    ]);
    setContractors(c.data); setClients(cl.data); setRespite(r.data);
    setVoiceStrength(vs.data || {});
  };
  useEffect(() => { load(); }, []);

  const contractorName = (id) => contractors.find((c) => c.id === id)?.name || "—";
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const revoke = async (id) => {
    if (!window.confirm("Revoke this contractor's access? They will be immediately signed out and unable to log back in.")) return;
    await api.post(`/users/providers/${id}/revoke`);
    load();
    toast.success("Access revoked");
  };
  const reinstate = async (id) => {
    await api.post(`/users/providers/${id}/reinstate`);
    load();
    toast.success("Access reinstated");
  };
  const cancelRespite = async (id) => {
    if (!window.confirm("Cancel this respite assignment? The respite provider will be signed out immediately.")) return;
    await api.delete(`/respite/assignments/${id}`);
    load();
    toast.success("Respite cancelled");
  };

  return (
    <div className="space-y-6" data-testid="admin-profiles">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-label">Directory</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Contractor / Client Profiles</h1>
          <p className="text-sm text-muted-foreground">Manage Contractors (host families), their household roster, client profiles, and temporary respite access.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <AssignRespiteDialog clients={clients} onCreated={load} />
          <AddClientDialog contractors={contractors} onCreated={load} />
          <AddContractorDialog onCreated={load} />
        </div>
      </div>

      <Tabs defaultValue="clients">
        <TabsList>
          <TabsTrigger value="clients" data-testid="profiles-tab-clients">Clients ({clients.length})</TabsTrigger>
          <TabsTrigger value="contractors" data-testid="profiles-tab-contractors">Contractors ({contractors.length})</TabsTrigger>
          <TabsTrigger value="respite" data-testid="profiles-tab-respite">Respite ({respite.filter(r => r.status === "active").length})</TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/admin/profiles/client/${c.id}`)}
                className="text-left clinical-card p-5 hover:bg-secondary transition-colors flex items-center gap-3"
                data-testid={`client-profile-card-${c.id}`}
              >
                <ClientAvatar
                  name={c.name}
                  photoUrl={c.photo_data_url}
                  className="h-14 w-14 rounded-full object-cover border border-primary/20"
                  placeholderClassName="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-bold text-lg border border-primary/20"
                  testId={`client-profile-card-${c.id}-photo`}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold leading-tight truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> {contractorName(c.contractor_id)}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="contractors" className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contractors.map((p) => (
              <Card key={p.id} className={`p-5 ${p.revoked ? "bg-red-50/40 border-red-200" : ""}`} data-testid={`contractor-card-${p.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="h-12 w-12 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                    <UserIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold leading-tight">{p.name}</p>
                      {p.revoked && <Badge variant="destructive">Revoked</Badge>}
                      <VoiceStrengthBadge count={voiceStrength[p.id]} />
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                    {p.address && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5"><MapPin className="w-3 h-3" /> {p.address}</p>
                    )}
                    {(p.phones || []).filter(ph => ph.number).slice(0, 2).map((ph) => (
                      <p key={`${ph.label || "phone"}-${ph.number}`} className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Phone className="w-3 h-3" /> {ph.label || "Phone"}: {ph.number}
                      </p>
                    ))}
                    {p.household_members?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Household: {p.household_members.length} member{p.household_members.length === 1 ? "" : "s"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/admin/profiles/contractor/${p.id}`)} data-testid={`view-contractor-${p.id}`}>View profile</Button>
                  {p.revoked ? (
                    <Button variant="outline" size="sm" onClick={() => reinstate(p.id)} data-testid={`reinstate-${p.id}`}>
                      <ShieldCheck className="w-4 h-4 mr-1 text-emerald-600" /> Reinstate
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => revoke(p.id)} className="text-red-600 hover:text-red-700" data-testid={`revoke-${p.id}`}>
                      <ShieldOff className="w-4 h-4 mr-1" /> Revoke access
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="respite" className="space-y-3">
          {respite.length === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="respite-empty">
              No respite assignments yet. Click "Assign Temporary Respite" above to provision one.
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {respite.map((a) => (
                <Card key={a.id} className={`p-5 ${a.status !== "active" ? "opacity-70" : ""}`} data-testid={`respite-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-display font-bold leading-tight">{a.respite_provider_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{a.respite_provider_email}</p>
                    </div>
                    <Badge variant={a.status === "active" ? "default" : "secondary"}>
                      {a.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <p><span className="font-semibold text-foreground">Client:</span> {clientName(a.client_id)}</p>
                    <p><span className="font-semibold text-foreground">Window:</span> {a.start_date} → {a.end_date}</p>
                  </div>
                  {a.status === "active" && (
                    <div className="flex gap-2 mt-3">
                      <Button variant="outline" size="sm"
                              onClick={() => cancelRespite(a.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`cancel-respite-${a.id}`}>
                        <ShieldOff className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
